import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import expenseService from '../../services/expenseService';
import { useTenant } from '../../contexts/TenantContext';
import {
  Box, Grid, Typography, Paper, Button, TextField, Select,
  MenuItem, FormControl, InputLabel, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  Dialog, DialogActions, DialogContent, DialogTitle,
  InputAdornment, Chip, CircularProgress, Alert, Snackbar,
  Card, CardContent, Divider, FormHelperText, TablePagination,
  Tab, Tabs, Container
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalAtm as LocalAtmIcon,
  Category as CategoryIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ReceiptLong as ReceiptLongIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Refresh as RefreshIcon,
  BarChart as BarChartIcon,
  StackedBarChart as StackedBarChartIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
// Import Recharts components
import { 
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, LineChart, Line, Area, AreaChart
} from 'recharts';

const ExpensesPage = () => {
  const { tenant } = useTenant();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentExpense, setCurrentExpense] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [expenseStats, setExpenseStats] = useState({
    totalExpenses: 0,
    thisMonthExpenses: 0,
    thisYearExpenses: 0,
    categoryCounts: {}
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: 'date',
    direction: 'desc'
  });
  const [filterConfig, setFilterConfig] = useState({
    category: 'all',
    startDate: '',
    endDate: '',
    searchTerm: ''
  });
  // Add state for chart options
  const [chartTab, setChartTab] = useState(0);
  const [monthlyExpenseData, setMonthlyExpenseData] = useState([]);
  const [categoryExpenseData, setCategoryExpenseData] = useState([]);

  const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    defaultValues: {
      amount: '',
      category: 'utilities',
      date: new Date().toISOString().split('T')[0],
      description: '',
      paymentMethod: 'bank_transfer',
      reference: '' // Leave empty to use auto-generated reference
    }
  });

  const expenseCategories = [
    { value: 'utilities', label: 'Utilities' },
    { value: 'rent', label: 'Rent/Lease' },
    { value: 'salaries', label: 'Salaries/Wages' },
    { value: 'supplies', label: 'Office Supplies' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'marketing', label: 'Marketing/Advertising' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'events', label: 'Events' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'professional_fees', label: 'Professional Fees' },
    { value: 'taxes', label: 'Taxes' },
    { value: 'meals', label: 'Meals/Entertainment' },
    { value: 'other', label: 'Other' }
  ];

  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'check', label: 'Check' },
    { value: 'mobile_payment', label: 'Mobile Payment' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchExpenses();
  }, []);

  useEffect(() => {
    // This effect ensures we update chart data whenever expenseStats changes
    if (expenses.length > 0) {
      prepareChartData(expenses);
    }
  }, [expenseStats]);

  // Add function to prepare chart data
  const prepareChartData = (expensesData) => {
    console.log("Preparing chart data with expenses:", expensesData);
    
    // Prepare category data for pie chart
    // Check if categoryCounts is populated
    console.log("Category counts:", expenseStats.categoryCounts);
    
    const categoryData = Object.entries(expenseStats.categoryCounts || {}).map(([category, data]) => {
      console.log(`Processing category ${category} with data:`, data);
      return {
        name: getCategoryLabel(category),
        value: data.amount || 0,
        count: data.count || 0
      };
    });
    
    console.log("Generated category data for chart:", categoryData);
    setCategoryExpenseData(categoryData);
    
    // Prepare monthly data for bar chart
    const monthlyData = [];
    const monthlyTotals = {};
    
    // Group expenses by month
    expensesData.forEach(expense => {
      const date = new Date(expense.date);
      const month = date.getMonth();
      const year = date.getFullYear();
      const monthYearKey = `${year}-${month+1}`;
      
      if (!monthlyTotals[monthYearKey]) {
        monthlyTotals[monthYearKey] = {
          month: new Date(year, month, 1).toLocaleString('default', { month: 'short', year: 'numeric' }),
          amount: 0,
          count: 0,
          monthSortKey: year * 100 + month  // for sorting
        };
      }
      
      monthlyTotals[monthYearKey].amount += parseFloat(expense.amount);
      monthlyTotals[monthYearKey].count += 1;
    });
    
    // Convert to array and sort by date
    const monthlyDataArray = Object.values(monthlyTotals)
      .sort((a, b) => a.monthSortKey - b.monthSortKey)
      .slice(-12); // Get last 12 months only
    
    setMonthlyExpenseData(monthlyDataArray);
  };

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const response = await expenseService.getExpenses();
      
      let expensesData = [];
      if (response && response.data) {
        expensesData = Array.isArray(response.data) ? response.data : response.data.expenses || [];
      } else if (Array.isArray(response)) {
        expensesData = response;
      }
      
      setExpenses(expensesData);
      
      // Calculate expense statistics
      calculateExpenseStats(expensesData);
      
      // Prepare chart data
      prepareChartData(expensesData);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load expenses: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateExpenseStats = (expensesData) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const categoryCounts = {};
    let totalExpenses = 0;
    let thisMonthExpenses = 0;
    let thisYearExpenses = 0;
    
    expensesData.forEach(expense => {
      const amount = parseFloat(expense.amount) || 0;
      totalExpenses += amount;
      
      // Add to category count
      if (categoryCounts[expense.category]) {
        categoryCounts[expense.category].count += 1;
        categoryCounts[expense.category].amount += amount;
      } else {
        categoryCounts[expense.category] = {
          count: 1,
          amount: amount
        };
      }
      
      // Check if expense is in current month/year
      const expenseDate = new Date(expense.date);
      if (expenseDate.getFullYear() === currentYear) {
        thisYearExpenses += amount;
        
        if (expenseDate.getMonth() === currentMonth) {
          thisMonthExpenses += amount;
        }
      }
    });
    
    setExpenseStats({
      totalExpenses,
      thisMonthExpenses,
      thisYearExpenses,
      categoryCounts
    });
  };

  const handleOpenAddDialog = () => {
    // Get current date in YYYY-MM-DD format for the local timezone
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const formattedToday = `${year}-${month}-${day}`;
    
    reset({
      amount: '',
      category: 'utilities',
      date: formattedToday,
      description: '',
      paymentMethod: 'bank_transfer',
      reference: ''
    });
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
  };

  const handleOpenEditDialog = (expense) => {
    setCurrentExpense(expense);
    setValue('amount', expense.amount);
    setValue('category', expense.category);
    
    // Format date for the date input (YYYY-MM-DD format)
    // If the date is already in YYYY-MM-DD format, use it directly
    const datePart = expense.date.split('T')[0];
    setValue('date', datePart);
    
    setValue('description', expense.description || '');
    setValue('paymentMethod', expense.paymentMethod || 'bank_transfer');
    setValue('reference', expense.reference || '');
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setCurrentExpense(null);
  };

  const handleOpenDeleteDialog = (expense) => {
    setCurrentExpense(expense);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCurrentExpense(null);
  };

  const handleAddExpense = async (data) => {
    try {
      setLoading(true);
      
      // Create a new date object in the user's timezone
      // Get date in YYYY-MM-DD format directly from the date picker
      const dateValue = data.date;
      console.log('Adding expense with date:', dateValue);
      
      const response = await expenseService.addExpense({
        ...data,
        // Use date string directly without conversion
        date: dateValue,
        amount: parseFloat(data.amount),
        tenantId: tenant?._id
      });
      
      setSnackbar({
        open: true,
        message: `Expense added successfully with reference: ${response.data.reference}`,
        severity: 'success'
      });
      
      handleCloseAddDialog();
      await fetchExpenses();
    } catch (error) {
      console.error('Error adding expense:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add expense: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditExpense = async (data) => {
    if (!currentExpense) return;
    
    try {
      setLoading(true);
      console.log('Updating expense with date:', data.date);
      
      const response = await expenseService.updateExpense(currentExpense._id, {
        ...data,
        // Use date string directly without conversion
        date: data.date,
        amount: parseFloat(data.amount)
      });
      
      setSnackbar({
        open: true,
        message: 'Expense updated successfully',
        severity: 'success'
      });
      
      handleCloseEditDialog();
      await fetchExpenses();
    } catch (error) {
      console.error('Error updating expense:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update expense: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExpense = async () => {
    if (!currentExpense) return;
    
    try {
      setLoading(true);
      await expenseService.deleteExpense(currentExpense._id);
      
      setSnackbar({
        open: true,
        message: 'Expense deleted successfully',
        severity: 'success'
      });
      
      handleCloseDeleteDialog();
      await fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete expense: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (field, value) => {
    setFilterConfig(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilterConfig({
      category: 'all',
      startDate: '',
      endDate: '',
      searchTerm: ''
    });
    setPage(0);
  };

  // Apply filtering
  const filteredExpenses = expenses.filter(expense => {
    // Category filter
    if (filterConfig.category !== 'all' && expense.category !== filterConfig.category) {
      return false;
    }
    
    // Date range filter
    if (filterConfig.startDate) {
      const startDate = new Date(filterConfig.startDate);
      const expenseDate = new Date(expense.date);
      if (expenseDate < startDate) {
        return false;
      }
    }
    
    if (filterConfig.endDate) {
      const endDate = new Date(filterConfig.endDate);
      endDate.setHours(23, 59, 59); // End of day
      const expenseDate = new Date(expense.date);
      if (expenseDate > endDate) {
        return false;
      }
    }
    
    // Search term filter
    if (filterConfig.searchTerm) {
      const searchTermLower = filterConfig.searchTerm.toLowerCase();
      return (
        expense.description?.toLowerCase().includes(searchTermLower) ||
        expense.reference?.toLowerCase().includes(searchTermLower) ||
        expense.category.toLowerCase().includes(searchTermLower) ||
        expense.amount.toString().includes(searchTermLower)
      );
    }
    
    return true;
  });

  // Apply sorting
  const sortedExpenses = [...filteredExpenses].sort((a, b) => {
    if (sortConfig.key === 'date') {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
    }
    
    if (sortConfig.key === 'amount') {
      const amountA = parseFloat(a.amount) || 0;
      const amountB = parseFloat(b.amount) || 0;
      return sortConfig.direction === 'asc' ? amountA - amountB : amountB - amountA;
    }
    
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Pagination
  const paginatedExpenses = sortedExpenses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Extract just the date part if it's an ISO string
    const datePart = dateString.split('T')[0];
    
    // Convert YYYY-MM-DD to local date format
    const [year, month, day] = datePart.split('-');
    return new Date(+year, +month - 1, +day).toLocaleDateString();
  };

  const getCategoryLabel = (categoryValue) => {
    const category = expenseCategories.find(cat => cat.value === categoryValue);
    return category ? category.label : categoryValue.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: { xs: 1, md: 4 }, 
        mb: 4,
        px: { xs: 2, md: 3 }
      }}
    >
      <Box sx={{ p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            p: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f44336 0%, #e57373 100%)',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
                <LocalAtmIcon sx={{ mr: 1 }} /> Expenses Management
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8 }}>
                Track and manage all expenses for {tenant?.name || 'your organization'}
              </Typography>
            </Box>
            <Button
              onClick={handleOpenAddDialog}
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                },
                color: 'white',
                fontWeight: 'medium'
              }}
            >
              Add Expense
            </Button>
          </Box>
        </Paper>

        <Paper elevation={2} sx={{ borderRadius: 2, mb: 4, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
              <LocalAtmIcon sx={{ mr: 1, color: 'error.main' }} /> Expense Overview
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <CardContent>
                  <Typography variant="overline" sx={{ opacity: 0.8 }}>
                    Total Expenses
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1, fontWeight: 'bold' }}>
                    {formatCurrency(expenseStats.totalExpenses)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    All-time expense spending
                  </Typography>
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      right: -20, 
                      bottom: -20, 
                      opacity: 0.2,
                      transform: 'rotate(-15deg)'
                    }}
                  >
                    <ReceiptLongIcon sx={{ fontSize: 100 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <CardContent>
                  <Typography variant="overline" sx={{ opacity: 0.8 }}>
                    This Month
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1, fontWeight: 'bold' }}>
                    {formatCurrency(expenseStats.thisMonthExpenses)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Expenses for {new Date().toLocaleString('default', { month: 'long' })}
                  </Typography>
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      right: -20, 
                      bottom: -20, 
                      opacity: 0.2,
                      transform: 'rotate(-15deg)'
                    }}
                  >
                    <CategoryIcon sx={{ fontSize: 100 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={3} sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <CardContent>
                  <Typography variant="overline" sx={{ opacity: 0.8 }}>
                    Categories
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1, fontWeight: 'bold' }}>
                    {Object.keys(expenseStats.categoryCounts).length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Different expense categories
                  </Typography>
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      right: -20, 
                      bottom: -20, 
                      opacity: 0.2,
                      transform: 'rotate(-15deg)'
                    }}
                  >
                    <ReceiptIcon sx={{ fontSize: 100 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Expense Charts Section */}
        <Paper elevation={2} sx={{ borderRadius: 2, mb: 4, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
              <BarChartIcon sx={{ mr: 1, color: 'primary.main' }} /> Expense Analytics
            </Typography>
          </Box>

          {/* Chart Tabs */}
          <Tabs
            value={chartTab}
            onChange={(e, newValue) => setChartTab(newValue)}
            aria-label="expense chart options"
            sx={{ 
              mb: 3,
              '& .MuiTab-root': {
                minHeight: '48px',
                fontWeight: 'medium',
              }
            }}
          >
            <Tab 
              icon={<BarChartIcon />} 
              iconPosition="start" 
              label="Category Distribution" 
              id="chart-tab-0"
              aria-controls="chart-tabpanel-0"
            />
            <Tab 
              icon={<BarChartIcon />} 
              iconPosition="start" 
              label="Monthly Expenses" 
              id="chart-tab-1"
              aria-controls="chart-tabpanel-1"
            />
            <Tab 
              icon={<StackedBarChartIcon />} 
              iconPosition="start" 
              label="Yearly Comparison" 
              id="chart-tab-2"
              aria-controls="chart-tabpanel-2"
            />
          </Tabs>

          {/* Chart Content */}
          <Box role="tabpanel" hidden={chartTab !== 0} id="chart-tabpanel-0" aria-labelledby="chart-tab-0">
            {chartTab === 0 && (
              <Box sx={{ mt: 2 }}>
                {categoryExpenseData.length > 0 ? (
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Category Breakdown
                    </Typography>
                    <TableContainer sx={{ maxHeight: 500 }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Category</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">%</TableCell>
                            <TableCell align="right">Count</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {categoryExpenseData
                            .sort((a, b) => b.value - a.value) // Sort by amount (highest first)
                            .map((category, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box 
                                      sx={{ 
                                        width: 12, 
                                        height: 12, 
                                        borderRadius: '50%', 
                                        mr: 1.5,
                                        bgcolor: (() => {
                                          const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#9C27B0', '#E91E63', '#673AB7', '#3F51B5', '#03A9F4'];
                                          return COLORS[index % COLORS.length];
                                        })()
                                      }} 
                                    />
                                    {category.name}
                                  </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                                  ${category.value.toFixed(2)}
                                </TableCell>
                                <TableCell align="right">
                                  <Chip 
                                    label={`${((category.value / expenseStats.totalExpenses) * 100).toFixed(1)}%`}
                                    size="small"
                                    sx={{ 
                                      fontWeight: 'medium',
                                      bgcolor: 'rgba(0, 0, 0, 0.05)'
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {category.count} transactions
                                </TableCell>
                              </TableRow>
                            ))}
                          {/* Add a total row at the bottom */}
                          <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.04)' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              ${expenseStats.totalExpenses.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              <Chip 
                                label="100%"
                                size="small"
                                color="primary"
                                sx={{ fontWeight: 'medium' }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {categoryExpenseData.reduce((sum, cat) => sum + cat.count, 0)} transactions
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                ) : (
                  <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography color="text.secondary">No expense data available to display</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box role="tabpanel" hidden={chartTab !== 1} id="chart-tabpanel-1" aria-labelledby="chart-tab-1">
            {chartTab === 1 && (
              <Box sx={{ mt: 2 }}>
                {monthlyExpenseData.length > 0 ? (
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Monthly Expense Trends
                    </Typography>
                    <Box sx={{ height: 350, width: '100%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart 
                          data={monthlyExpenseData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="month" 
                            angle={-45} 
                            textAnchor="end" 
                            height={70}
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis 
                            tickFormatter={(value) => `$${value}`}
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip 
                            formatter={(value) => [`$${value.toFixed(2)}`, 'Amount']}
                            contentStyle={{ 
                              borderRadius: 8, 
                              border: 'none', 
                              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                              padding: '10px 15px'
                            }}
                          />
                          <Legend />
                          <Bar 
                            dataKey="amount" 
                            name="Expense Amount" 
                            fill="#f44336" 
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                ) : (
                  <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography color="text.secondary">No monthly data available to display chart</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box role="tabpanel" hidden={chartTab !== 2} id="chart-tabpanel-2" aria-labelledby="chart-tab-2">
            {chartTab === 2 && (
              <Box sx={{ mt: 2 }}>
                {monthlyExpenseData.length > 0 ? (
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Yearly Comparison (Count vs Amount)
                    </Typography>
                    <Box sx={{ height: 350, width: '100%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={monthlyExpenseData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="month" 
                            angle={-45} 
                            textAnchor="end" 
                            height={70}
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis 
                            yAxisId="left"
                            tickFormatter={(value) => `$${value}`}
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis 
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip 
                            formatter={(value, name) => [
                              name === "amount" ? `$${value.toFixed(2)}` : value,
                              name === "amount" ? "Total Amount" : "Transaction Count"
                            ]}
                            contentStyle={{ 
                              borderRadius: 8, 
                              border: 'none', 
                              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                              padding: '10px 15px'
                            }}
                          />
                          <Legend />
                          <Area 
                            type="monotone" 
                            dataKey="amount" 
                            name="Total Amount" 
                            stroke="#8884d8" 
                            fill="#8884d8" 
                            yAxisId="left"
                            fillOpacity={0.6}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="count" 
                            name="Transaction Count" 
                            stroke="#82ca9d" 
                            fill="#82ca9d" 
                            yAxisId="right"
                            fillOpacity={0.6}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                ) : (
                  <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography color="text.secondary">No yearly data available to display chart</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Paper>

        <Paper elevation={2} sx={{ borderRadius: 2, mb: 4, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Typography variant="h6" sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
              <FilterListIcon sx={{ mr: 1, fontSize: 20 }} /> Filters
            </Typography>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="category-filter-label">Category</InputLabel>
              <Select
                labelId="category-filter-label"
                id="category-filter"
                value={filterConfig.category}
                label="Category"
                onChange={(e) => handleFilterChange('category', e.target.value)}
                size="small"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {expenseCategories.map(category => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Start Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filterConfig.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              sx={{ minWidth: 180 }}
            />
            
            <TextField
              label="End Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filterConfig.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              sx={{ minWidth: 180 }}
            />
            
            <TextField
              placeholder="Search expenses..."
              size="small"
              value={filterConfig.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              sx={{ flexGrow: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <Button 
              variant="outlined" 
              size="small"
              onClick={handleClearFilters}
              startIcon={<RefreshIcon />}
            >
              Clear Filters
            </Button>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : paginatedExpenses.length > 0 ? (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ bgcolor: 'rgba(0, 0, 0, 0.04)' }}>
                    <TableRow>
                      <TableCell>
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                          onClick={() => handleSort('date')}
                        >
                          Date
                          {sortConfig.key === 'date' && (
                            sortConfig.direction === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                          onClick={() => handleSort('category')}
                        >
                          Category
                          {sortConfig.key === 'category' && (
                            sortConfig.direction === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                          onClick={() => handleSort('amount')}
                        >
                          Amount
                          {sortConfig.key === 'amount' && (
                            sortConfig.direction === 'asc' ? 
                              <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                              <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>Payment Method</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedExpenses.map((expense) => (
                      <TableRow
                        key={expense._id}
                        sx={{
                          '&:nth-of-type(odd)': { bgcolor: 'background.default' },
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {formatDate(expense.date)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getCategoryLabel(expense.category)}
                            size="small"
                            sx={{ 
                              bgcolor: (() => {
                                switch(expense.category) {
                                  case 'utilities': return 'rgba(33, 150, 243, 0.1)';
                                  case 'rent': return 'rgba(156, 39, 176, 0.1)';
                                  case 'salaries': return 'rgba(76, 175, 80, 0.1)';
                                  case 'supplies': return 'rgba(255, 152, 0, 0.1)';
                                  case 'marketing': return 'rgba(233, 30, 99, 0.1)';
                                  case 'events': return 'rgba(0, 188, 212, 0.1)';
                                  default: return 'rgba(158, 158, 158, 0.1)';
                                }
                              })(),
                              color: (() => {
                                switch(expense.category) {
                                  case 'utilities': return 'rgb(25, 118, 210)';
                                  case 'rent': return 'rgb(123, 31, 162)';
                                  case 'salaries': return 'rgb(56, 142, 60)';
                                  case 'supplies': return 'rgb(245, 124, 0)';
                                  case 'marketing': return 'rgb(173, 20, 87)';
                                  case 'events': return 'rgb(0, 151, 167)';
                                  default: return 'rgb(97, 97, 97)';
                                }
                              })()
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography 
                            sx={{ 
                              maxWidth: 200, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap' 
                            }}
                            title={expense.description}
                          >
                            {expense.description || 'No description'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 'medium',
                              color: 'error.main'
                            }}
                          >
                            {formatCurrency(expense.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {expense.paymentMethod?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                        </TableCell>
                        <TableCell>{expense.reference || '-'}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(expense)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(expense)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredExpenses.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          ) : (
            <Box sx={{ 
              p: 4, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <ReceiptLongIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No expenses found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {filteredExpenses.length === 0 && expenses.length > 0 ? 
                  'No expenses match your search criteria. Try adjusting the filters.' : 
                  'Start by adding your first expense to track your organization\'s spending.'
                }
              </Typography>
              {filteredExpenses.length === 0 && expenses.length > 0 ? (
                <Button 
                  variant="outlined" 
                  onClick={handleClearFilters}
                  startIcon={<RefreshIcon />}
                >
                  Clear Filters
                </Button>
              ) : (
                <Button 
                  variant="contained" 
                  onClick={handleOpenAddDialog}
                  startIcon={<AddIcon />}
                >
                  Add Your First Expense
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </Box>
      
      {/* Add Expense Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <form onSubmit={handleSubmit(handleAddExpense)}>
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <AddIcon sx={{ mr: 1 }} />
              Add New Expense
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="amount"
                  control={control}
                  rules={{ 
                    required: 'Amount is required',
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: 'Enter a valid amount'
                    } 
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Amount"
                      type="number"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="date"
                  control={control}
                  rules={{ required: 'Date is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="category"
                  control={control}
                  rules={{ required: 'Category is required' }}
                  render={({ field, fieldState }) => (
                    <FormControl fullWidth error={!!fieldState.error}>
                      <InputLabel id="category-label">Category</InputLabel>
                      <Select
                        {...field}
                        labelId="category-label"
                        label="Category"
                      >
                        {expenseCategories.map(category => (
                          <MenuItem key={category.value} value={category.value}>
                            {category.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldState.error && (
                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="paymentMethod"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel id="payment-method-label">Payment Method</InputLabel>
                      <Select
                        {...field}
                        labelId="payment-method-label"
                        label="Payment Method"
                      >
                        {paymentMethods.map(method => (
                          <MenuItem key={method.value} value={method.value}>
                            {method.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      multiline
                      rows={2}
                      fullWidth
                      placeholder="Provide details about this expense"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="reference"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Reference Number/Receipt ID"
                      fullWidth
                      placeholder="Leave empty for auto-generated reference ID"
                      helperText="Optional: Provide a custom reference or leave empty for auto-generation"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseAddDialog} variant="outlined">Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Saving...' : 'Add Expense'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Expense Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <form onSubmit={handleSubmit(handleEditExpense)}>
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <EditIcon sx={{ mr: 1 }} />
              Edit Expense
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="amount"
                  control={control}
                  rules={{ 
                    required: 'Amount is required',
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: 'Enter a valid amount'
                    } 
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Amount"
                      type="number"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="date"
                  control={control}
                  rules={{ required: 'Date is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="category"
                  control={control}
                  rules={{ required: 'Category is required' }}
                  render={({ field, fieldState }) => (
                    <FormControl fullWidth error={!!fieldState.error}>
                      <InputLabel id="category-label">Category</InputLabel>
                      <Select
                        {...field}
                        labelId="category-label"
                        label="Category"
                      >
                        {expenseCategories.map(category => (
                          <MenuItem key={category.value} value={category.value}>
                            {category.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldState.error && (
                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="paymentMethod"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel id="payment-method-label">Payment Method</InputLabel>
                      <Select
                        {...field}
                        labelId="payment-method-label"
                        label="Payment Method"
                      >
                        {paymentMethods.map(method => (
                          <MenuItem key={method.value} value={method.value}>
                            {method.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      multiline
                      rows={2}
                      fullWidth
                      placeholder="Provide details about this expense"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="reference"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Reference Number/Receipt ID"
                      fullWidth
                      placeholder="e.g., Invoice #12345 or Receipt ID"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseEditDialog} variant="outlined">Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Saving...' : 'Update Expense'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Expense Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Typography sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Confirm Deletion
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this expense?
          </Typography>
          {currentExpense && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Date:</strong> {formatDate(currentExpense.date)}
              </Typography>
              <Typography variant="body2">
                <strong>Category:</strong> {getCategoryLabel(currentExpense.category)}
              </Typography>
              <Typography variant="body2">
                <strong>Amount:</strong> {formatCurrency(currentExpense.amount)}
              </Typography>
              {currentExpense.description && (
                <Typography variant="body2">
                  <strong>Description:</strong> {currentExpense.description}
                </Typography>
              )}
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDeleteDialog} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteExpense} 
            variant="contained" 
            color="error"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {loading ? 'Deleting...' : 'Delete Expense'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ExpensesPage;
